import React, { useMemo } from 'react';
import VisaApplicationFilters from './VisaApplicationFilters';
import {
  filterVisaApplications,
  getApplicantName,
} from '../utils/visaApplicationFilters';
import { formatStatusLabel, getStatusBadgeClass } from '../utils/visaApplicationStatus';
import { isFormFilled } from '../utils/visaApplicationData';

const VisaApplicationsPoolSection = ({
  title,
  subtitle,
  applications,
  filters,
  onFiltersChange,
  onClearFilters,
  showCompanyFilter = false,
  showManagerFilter = false,
  managerOptions = [],
  showCompanyColumn = false,
  onView,
  onViewHistory,
  onFillForm,
  canFillForm,
  onUpdateStatus,
  emptyMessage = 'No applications in this pool yet.',
  headerExtra = null,
  hideStatusFilter = true,
}) => {
  const filteredApplications = useMemo(
    () => filterVisaApplications(applications, filters),
    [applications, filters]
  );

  const canUpdateStatus = (app) =>
    onUpdateStatus && ['processing', 'error', 'finalize'].includes(app.application_status);

  const showFillForm = (app) => {
    if (!onFillForm) return false;
    if (String(app.application_status || '').toLowerCase() === 'finalize') return false;
    if (typeof canFillForm === 'function') return canFillForm(app);
    return true;
  };

  const colSpan = showCompanyColumn ? 9 : 8;

  return (
    <>
      <div className="mb-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="primary-title">{title}</h1>
            <p className="text-ink-secondary text-sm mt-1">{subtitle}</p>
          </div>
          {headerExtra}
        </div>
      </div>

      <VisaApplicationFilters
        applications={applications}
        filters={filters}
        onChange={onFiltersChange}
        onClear={onClearFilters}
        showCompanyFilter={showCompanyFilter}
        showManagerFilter={showManagerFilter}
        managerOptions={managerOptions}
        hideStatusFilter={hideStatusFilter}
        totalCount={applications.length}
        filteredCount={filteredApplications.length}
      />

      <div className="table-shell">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                {showCompanyColumn && (
                  <th>Company</th>
                )}
                <th>Passenger ID</th>
                <th>Applicant</th>
                <th>From</th>
                <th>To</th>
                <th>Visa Type</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {applications.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="text-center py-8 text-gray-400">
                    {emptyMessage}
                  </td>
                </tr>
              ) : filteredApplications.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="text-center py-8 text-gray-400">
                    No applications match your filters.
                  </td>
                </tr>
              ) : (
                filteredApplications.map((app) => {
                  const filled = app.form_filled ?? isFormFilled(app.customer_visa_application_data);

                  return (
                  <tr key={app.id}>
                    {showCompanyColumn && (
                      <td className="font-semibold text-ink">{app.company_name}</td>
                    )}
                    <td className="font-semibold text-ink">{app.passenger_id}</td>
                    <td>
                      {filled ? (
                        <span className="inline-flex items-center gap-2 flex-wrap">
                          <span>{getApplicantName(app.customer_visa_application_data)}</span>
                          {(app.form_version_label || app.form_version > 0) && (
                            <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-indigo-50 text-indigo-700">
                              {app.form_version_label || `v${app.form_version}`}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-amber-600">Form pending</span>
                      )}
                    </td>
                    <td>{app.from_country_name || app.from_country}</td>
                    <td>{app.to_country_name || app.to_country}</td>
                    <td>{app.visa_type}</td>
                    <td>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(app.application_status)}`}
                      >
                        {formatStatusLabel(app.application_status)}
                      </span>
                    </td>
                    <td>{new Date(app.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="flex gap-2 flex-wrap">
                        {showFillForm(app) && (
                          <button
                            type="button"
                            onClick={() => onFillForm(app)}
                            className="action-chip"
                          >
                            {filled ? 'Update Form' : 'Fill Form'}
                          </button>
                        )}
                        {filled && onView && (
                          <button
                            type="button"
                            onClick={() => onView(app)}
                            className="action-chip"
                          >
                            View
                          </button>
                        )}
                        {onViewHistory && (
                          <button
                            type="button"
                            onClick={() => onViewHistory(app)}
                            className="action-chip"
                          >
                            History
                          </button>
                        )}
                        {canUpdateStatus(app) && (
                          <button
                            type="button"
                            onClick={() => onUpdateStatus(app)}
                            className="btn-outline"
                          >
                            Update Status
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default VisaApplicationsPoolSection;
